import React, { useCallback, useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { GridViewOutlined, TableRowsOutlined } from "@mui/icons-material";
import { radius, tokens } from "../theme/theme";

/**
 * Таблиця / Картки switch, remembered per list.
 *
 * Neither view is better in general: the grid wins for scanning a hundred rows,
 * sorting by price or comparing numbers; the cards win for recognising a flat
 * from its photo. So the choice is the realtor's, and it should survive a
 * reload — being silently put back into the other view every morning is worse
 * than not having the choice at all.
 */

const PREFIX = "leocity.view.";

export function useViewMode(listKey, fallback) {
  const storageKey = PREFIX + listKey;

  const [mode, setMode] = useState(function () {
    try {
      return window.localStorage.getItem(storageKey) || fallback || "table";
    } catch (err) {
      // Private mode and locked-down browsers both throw here.
      return fallback || "table";
    }
  });

  useEffect(
    function () {
      try {
        window.localStorage.setItem(storageKey, mode);
      } catch (err) {
        /* the choice just will not persist; not worth surfacing */
      }
    },
    [storageKey, mode]
  );

  const choose = useCallback(function (next) {
    // ToggleButtonGroup hands back null when the active button is clicked
    // again; keeping the current mode is better than rendering nothing.
    if (next) setMode(next);
  }, []);

  return [mode, choose];
}

export default function ViewToggle({ mode, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={mode}
      onChange={function (event, next) {
        onChange(next);
      }}
      sx={{
        "& .MuiToggleButton-root": {
          border: "1px solid " + tokens.line,
          borderRadius: radius.md,
          px: 1.4,
          color: tokens.ink500,
          "&.Mui-selected": {
            backgroundColor: tokens.blue50,
            color: tokens.blue700,
            "&:hover": { backgroundColor: tokens.blue100 },
          },
        },
      }}
    >
      <ToggleButton value="table" aria-label="Таблиця">
        <Tooltip title="Таблиця">
          <TableRowsOutlined sx={{ fontSize: 19 }} />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="cards" aria-label="Картки">
        <Tooltip title="Картки з фото">
          <GridViewOutlined sx={{ fontSize: 19 }} />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}
